import { useCallback, useEffect, useRef } from "react";

import {
  AnnotationOverlay,
  type AnnotationOverlayHandle,
} from "./annotation-overlay";
import { AnnotationToolbar } from "./annotation-toolbar";
import { useAnnotationStore } from "@/stores/annotation-store";
import { useSlideStore } from "@/stores/slide-store";
import {
  useSlideAnnotations,
  type AnnotationRole,
} from "@/hooks/use-slide-annotations";

interface SlideAnnotationLayerProps {
  // The slide iframe this layer anchors to.
  iframeRef: React.RefObject<HTMLIFrameElement | null>;
  // Scopes the BC channel: `optidev-slide-sync:{projectId}`. Pass null
  // to disable annotations entirely (renders nothing, no channel opens).
  projectId: string | null | undefined;
  // "presenter" → bidirectional + renders toolbar. drauu chunk is lazy-
  //               loaded only after the first tool click so non-drawing
  //               sessions never pay the ~5KB cost.
  // "viewer"    → display-only. drauu mounts immediately so incoming BC
  //               strokes render the moment the layer becomes visible.
  role: AnnotationRole;
  // When false, calls clearAll() on the annotation store — session-
  // ephemeral discard pattern (Canva). Pass `isPresentMode` to wipe
  // annotations on End Presentation. Defaults to true.
  active?: boolean;
  // Presenter-only toolbar positioning + slide navigation.
  toolbarClassName?: string;
  onPrev?: () => void;
  onNext?: () => void;
  canPrev?: boolean;
  canNext?: boolean;
}

// Single drop-in component that wires the full pen-annotation feature
// onto a slide iframe surface. Replaces the inline PresenterAnnotationLayer
// and EditorPresentAnnotationLayer helpers — adding annotations to any
// new surface is now a one-liner.
//
// Usage:
//   <SlideAnnotationLayer iframeRef={...} projectId={projectId} role="presenter" />
//   <SlideAnnotationLayer iframeRef={...} projectId={projectId} role="viewer" active={isPresentMode} />
//
// Internals:
//   - Owns the useSlideAnnotations hook (BC bridge + store sync).
//   - Reads currentSlideIndex from useSlideStore — caller doesn't pipe it.
//   - Reads activeTool from useAnnotationStore for the lazy-mount gate.
//   - For presenter: AnnotationOverlay + AnnotationToolbar.
//   - For viewer:    AnnotationOverlay only, mode=render-only.
//   - Lifecycle: when `active` flips to false, runs clearAll().
export function SlideAnnotationLayer({
  iframeRef,
  projectId,
  role,
  active = true,
  toolbarClassName,
  onPrev,
  onNext,
  canPrev,
  canNext,
}: SlideAnnotationLayerProps) {
  const currentSlideIndex = useSlideStore((s) => s.currentSlideIndex);
  const activeTool = useAnnotationStore((s) => s.activeTool);
  const annotations = useSlideAnnotations({ projectId, role });
  const overlayRef = useRef<AnnotationOverlayHandle | null>(null);

  // Clear handler — the presenter's local drauu is the source of truth
  // for in-progress strokes (we deliberately don't write each commit
  // back to the local store; see annotation-overlay.tsx onChange
  // comments). So a Clear click needs to (a) imperatively wipe drauu
  // and (b) drive the hook's BC + store path so peers + future slide-
  // nav save use the cleared state. For viewers the imperative call is
  // a no-op — viewer's drauu reacts via liveSnapshot transitioning to
  // undefined inside the hook's clearSlide.
  const handleClear = useCallback(() => {
    overlayRef.current?.clearCanvas();
    annotations.onClear();
  }, [annotations.onClear]);

  // Presenter-only lazy gate: defer the AnnotationOverlay mount (and the
  // drauu chunk it loads) until the user clicks the Pen for the first
  // time. After first activation we keep the overlay mounted so in-
  // session strokes survive toggling the toolbar off and back on.
  const hasEverActivatedRef = useRef(false);
  if (activeTool !== null) hasEverActivatedRef.current = true;
  const shouldMountOverlay =
    role === "viewer" || hasEverActivatedRef.current;

  // End-Presentation cleanup. Caller passes the appropriate active
  // boolean — e.g. `isPresentMode` for the editor's in-page overlay so
  // exiting Present wipes the in-memory strokes.
  useEffect(() => {
    if (active) return;
    useAnnotationStore.getState().clearAll();
  }, [active]);

  return (
    <>
      {shouldMountOverlay && (
        <AnnotationOverlay
          ref={overlayRef}
          iframeRef={iframeRef}
          slideIndex={currentSlideIndex}
          mode={role === "presenter" ? "interactive" : "render-only"}
          enabled={role === "presenter" && activeTool !== null}
          onChange={role === "presenter" ? annotations.onChange : undefined}
        />
      )}
      {role === "presenter" && (
        <AnnotationToolbar
          slideIndex={currentSlideIndex}
          className={toolbarClassName}
          onClear={handleClear}
          onPrev={onPrev}
          onNext={onNext}
          canPrev={canPrev}
          canNext={canNext}
        />
      )}
    </>
  );
}
